import { processListingParams } from '@/api/helpers';
import { FiltersType } from '@/api/types';
import {
  Account,
  AccountAPIConfig,
  AccountSendTestEmailRequest,
  AccountSSOConfig,
  ImportSSOConfigRequest,
  ListAccountsRequest,
  ListAccountsResponse,
  ListDuplicatedUsersRequest,
  ListDuplicatedUsersResponse,
  SaveAccountRequest,
  SubdomainAccountIdResponse,
  UpdateAccountAPIClientRequest,
  UpdateAccountRequest,
  UpdateAccountTableSettingsRequest,
  UpdateSSOConfigRequest,
} from '@/client/accounts';
import { getExceptionFromAxiosError } from '@/client/helpers';
import { MessageResponseModel } from '@/client/models';
import { SendingProfileType } from '@/client/sender-profiles';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { omit } from 'lodash';

export const accountsService = (client: AxiosInstance) => {
  const ACCOUNTS_BASE_URL = '/accounts';
  const SUBDOMAINS_BASE_URL = '/subdomains';

  const getAccounts = async (
    params: ListAccountsRequest,
  ): Promise<ListAccountsResponse> => {
    try {
      const result = await client.get<
        ListAccountsResponse,
        AxiosResponse<ListAccountsResponse>
      >(ACCOUNTS_BASE_URL, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getAccount = async (accountId?: string): Promise<Account> => {
    try {
      const result = await client.get<Account, AxiosResponse<Account>>(
        `${ACCOUNTS_BASE_URL}/${accountId}`,
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const saveAccount = async (request: SaveAccountRequest): Promise<Account> => {
    try {
      const result = await client.post<Account, AxiosResponse<Account>>(
        ACCOUNTS_BASE_URL,
        request,
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const updateAccount = async (
    request: UpdateAccountRequest,
  ): Promise<Account> => {
    try {
      const result = await client.patch<Account, AxiosResponse<Account>>(
        `${ACCOUNTS_BASE_URL}/${request.accountId}`,
        request.updates,
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const deleteAccount = async (
    accountId: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.delete<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${ACCOUNTS_BASE_URL}/${accountId}`, {
        withCredentials: true,
      });

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getDuplicatedUsers = async (
    params: ListDuplicatedUsersRequest,
    accountId?: string,
  ): Promise<ListDuplicatedUsersResponse> => {
    try {
      const result = await client.get<
        ListDuplicatedUsersResponse,
        AxiosResponse<ListDuplicatedUsersResponse>
      >(`${ACCOUNTS_BASE_URL}/${accountId}/users/migration`, {
        withCredentials: true,
        params: processListingParams(params),
      });
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const accountsNotifyUrl = (accountId: string) =>
    `${client.defaults.baseURL}accounts/${accountId}/notify`;

  const getSubdomain = async (
    subdomain: string,
  ): Promise<SubdomainAccountIdResponse> => {
    try {
      const result = await client.get<
        SubdomainAccountIdResponse,
        AxiosResponse<SubdomainAccountIdResponse>
      >(`${SUBDOMAINS_BASE_URL}/${subdomain}`, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const sendWhitelistingTestEmail = async (
    accountId: string,
    body: {
      recipientEmail: string;
      recipientName: string;
      sendingProfileType: SendingProfileType;
    },
  ): Promise<{ message: string; jobId: string }> => {
    try {
      const result = await client.post<
        { message: string; jobId: string },
        AxiosResponse<{ message: string; jobId: string }>
      >(
        `${ACCOUNTS_BASE_URL}/${accountId}/whitelisting/send-test-email`,
        body,
        { withCredentials: true },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const sendTestEmail = async (
    request: AccountSendTestEmailRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(
        `${ACCOUNTS_BASE_URL}/${request.accountId}/email-settings/test-email`,
        omit(request, 'accountId'),
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const importUsers = async (
    accountId: string,
    fileId: string,
    importKey: string,
    dryRun?: boolean,
    skipNew?: boolean,
    skipExisting?: boolean,
    deleteNotInFile?: boolean,
    restoreSoftDeleted?: boolean,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(
        `${ACCOUNTS_BASE_URL}/${accountId}/users/import`,
        {
          fileId,
          importKey,
          dryRun,
          skipNew,
          skipExisting,
          deleteNotInFile,
          restoreSoftDeleted,
        },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw e as AxiosError;
    }
  };

  const getSSOConfig = async (
    accountId?: string,
  ): Promise<AccountSSOConfig> => {
    try {
      const result = await client.get<
        AccountSSOConfig,
        AxiosResponse<AccountSSOConfig>
      >(`${ACCOUNTS_BASE_URL}/${accountId}/sso`, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const importSSOConfig = async (
    request: ImportSSOConfigRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(
        `${ACCOUNTS_BASE_URL}/${request.accountId}/sso/import`,
        omit(request, 'accountId'),
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const updateSSOConfig = async (
    request: UpdateSSOConfigRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.patch<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(
        `${ACCOUNTS_BASE_URL}/${request.accountId}/sso`,
        omit(request, 'accountId'),
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getAccountAPIClient = async (
    accountId?: string,
  ): Promise<AccountAPIConfig> => {
    try {
      const result = await client.get<
        AccountAPIConfig,
        AxiosResponse<AccountAPIConfig>
      >(`${ACCOUNTS_BASE_URL}/${accountId}/client`, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const updateAccountAPIClient = async (
    accountId: string,
    request: UpdateAccountAPIClientRequest,
  ): Promise<AccountAPIConfig> => {
    try {
      const result = await client.patch<
        AccountAPIConfig,
        AxiosResponse<AccountAPIConfig>
      >(`${ACCOUNTS_BASE_URL}/${accountId}/client`, request, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getTransportMapEntries = async (
    accountId: string,
  ): Promise<{ domain: string; fqdn: string }[]> => {
    try {
      const result = await client.get<
        { domain: string; fqdn: string }[],
        AxiosResponse<{ domain: string; fqdn: string }[]>
      >(
        `${ACCOUNTS_BASE_URL}/${accountId}/mail-connector/transport-map-entries`,
        { withCredentials: true },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const setupMailConnector = async (
    accountId: string,
    entries: { domain: string; fqdn: string }[],
  ): Promise<{ message: string; jobId: string }> => {
    try {
      const result = await client.post<
        { message: string; jobId: string },
        AxiosResponse<{ message: string; jobId: string }>
      >(
        `${ACCOUNTS_BASE_URL}/${accountId}/mail-connector/setup`,
        { entries },
        { withCredentials: true },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const ssoNotifyUrl = (accountId: string) =>
    `${client.defaults.baseURL}accounts/${accountId}/sso/notify`;

  const updateTableSettings = async (
    request: UpdateAccountTableSettingsRequest,
  ): Promise<Account> => {
    try {
      const result = await client.patch<Account, AxiosResponse<Account>>(
        `${ACCOUNTS_BASE_URL}/${request.accountId}/table-settings`,
        {
          personalizedTableViews: request.personalizedTableViews,
        },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const revertCompanyDescription = async (
    accountId: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${ACCOUNTS_BASE_URL}/${accountId}/revert-company-description`, null, {
        withCredentials: true,
      });

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const exportAccounts = async (request: {
    exportKey: string;
    filters?: FiltersType;
    companyOwnerName?: string;
  }): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${ACCOUNTS_BASE_URL}/export`, request, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const extendFreeTrial = async (): Promise<Account> => {
    try {
      const result = await client.post<Account, AxiosResponse<Account>>(
        `${ACCOUNTS_BASE_URL}/extend-trial`,
        null,
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  return {
    getAccounts,
    getAccount,
    saveAccount,
    updateAccount,
    deleteAccount,
    accountsNotifyUrl,
    getSubdomain,
    sendWhitelistingTestEmail,
    sendTestEmail,
    importUsers,
    getSSOConfig,
    importSSOConfig,
    updateSSOConfig,
    ssoNotifyUrl,
    getDuplicatedUsers,
    getAccountAPIClient,
    updateAccountAPIClient,
    updateTableSettings,
    setupMailConnector,
    getTransportMapEntries,
    revertCompanyDescription,
    exportAccounts,
    extendFreeTrial,
  };
};
